import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import CourtReserveIntegration from '../components/integrations/CourtReserveIntegration';
import CourtReserveAutoApprovalIntegration, {
  CourtReserveAutoApprovalAvailableCard,
  CourtReserveAutoApprovalConnectedCard
} from '../components/integrations/CourtReserveAutoApprovalIntegration';
import PlayByPointAutoApprovalIntegration, {
  PlayByPointAutoApprovalAvailableCard,
  PlayByPointAutoApprovalConnectedCard
} from '../components/integrations/PlayByPointAutoApprovalIntegration';
import PlayByPointIntegration from '../components/integrations/PlayByPointIntegration';
import SmashParkIntegration from '../components/integrations/SmashParkIntegration';
import BlueSkyIntegration from '../components/integrations/BlueSkyIntegration';
import CourtsAndCommonsIntegration from '../components/integrations/CourtsAndCommonsIntegration';
import PryorsPizzaKitchenIntegration from '../components/integrations/PryorsPizzaKitchenIntegration';
import ChickenNPickleIntegration from '../components/integrations/ChickenNPickleIntegration';
import PrairieBrewingIntegration from '../components/integrations/PrairieBrewingIntegration';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { api } from '../api/client';

import courtReserveLogo from '../assets/integrations/courtreserve.svg';
import playByPointLogo from '../assets/integrations/playbypoint.webp';
import googleCalendarLogo from '../assets/integrations/google-calendar.png';

interface IntegrationDef {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'coming_soon' | 'available' | 'beta';
  logoSrc?: string;
  logoAlt?: string;
}

interface ConnectedIntegration {
  id: string;
  integration_type: string;
  external_id: string;
  config: any;
  status: string;
  sync_status: string;
  auto_sync_enabled: boolean;
  sync_interval_minutes: number;
  sync_active_start_hour: number | null;
  sync_active_end_hour: number | null;
  last_sync_at: string | null;
  last_error: string | null;
}

// Superadmin-only one-off integrations not shown to regular users
const internalIntegrationDefs: IntegrationDef[] = [
  {
    id: 'courtreserve_auto_approval',
    name: 'CourtReserve Auto-Approval',
    description: 'Automatically create CourtReserve reservations and approve request-to-book bookings.',
    category: 'Booking Automation',
    status: 'available',
    logoSrc: courtReserveLogo,
    logoAlt: 'CourtReserve logo',
  },
  {
    id: 'playbypoint_auto_approval',
    name: 'PlayByPoint Auto-Approval',
    description: 'Automatically create PlayByPoint reservations and approve request-to-book bookings.',
    category: 'Booking Automation',
    status: 'available',
    logoSrc: playByPointLogo,
    logoAlt: 'PlayByPoint logo',
  },
  {
    id: 'smashpark',
    name: 'Smash Park',
    description: 'Real-time pickleball court availability sync for Smash Park venues.',
    category: 'Booking Sync',
    status: 'available',
  },
  {
    id: 'alleytrak',
    name: 'Blue Sky',
    description: 'Venue-specific AlleyTrak sync for Blue Sky Patio & Pickleball in Omaha.',
    category: 'Booking Sync',
    status: 'available',
  },
  {
    id: 'courtsandcommons',
    name: 'Courts & Commons',
    description: 'Venue-specific custom-site sync for Courts & Commons in Bixby.',
    category: 'Booking Sync',
    status: 'available',
  },
  {
    id: 'squareappointments',
    name: 'Pryor\'s Pizza Kitchen',
    description: 'Venue-specific Square Appointments sync for Pryor\'s Pizza Kitchen in Norman.',
    category: 'Booking Sync',
    status: 'available',
  },
  {
    id: 'chickennpickle',
    name: 'Chicken N Pickle',
    description: 'Real-time pickleball court availability sync for all 13 Chicken N Pickle locations.',
    category: 'Booking Sync',
    status: 'available',
  },
  {
    id: 'prairiebrewing',
    name: 'Prairie Artisan Ales',
    description: 'Venue-specific Square Appointments sync for Prairie Artisan Ales in Oklahoma City.',
    category: 'Booking Sync',
    status: 'available',
  },
];

const integrationDefs: IntegrationDef[] = [
  {
    id: 'courtreserve',
    name: 'CourtReserve',
    description: 'Real-time schedule and court syncing (availability + court mappings).',
    category: 'Booking Sync',
    status: 'beta',
    logoSrc: courtReserveLogo,
    logoAlt: 'CourtReserve logo',
  },
  {
    id: 'playbypoint',
    name: 'PlayByPoint',
    description: 'Real-time schedule and court syncing (availability + court mappings).',
    category: 'Booking Sync',
    status: 'beta',
    logoSrc: playByPointLogo,
    logoAlt: 'PlayByPoint logo',
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Real-time schedule syncing (coming soon).',
    category: 'Calendar',
    status: 'coming_soon',
    logoSrc: googleCalendarLogo,
    logoAlt: 'Google Calendar logo',
  },
];

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function SyncStatusBadge({ status }: { status: string }) {
  const label =
    status === 'queued' ? 'Queued' :
    status === 'syncing' ? 'Syncing' :
    status === 'failed' ? 'Error' :
    'Active';
  return (
    <span className={`integration-sync-badge integration-sync-badge--${status || 'idle'}`}>
      {status === 'syncing' && <span className="integration-sync-badge__spinner" />}
      {label}
    </span>
  );
}

function ConnectedCard({
  def,
  connected,
  onDisconnect,
  onRefresh,
  isSuperAdmin
}: {
  def: IntegrationDef;
  connected: ConnectedIntegration;
  onDisconnect: () => void;
  onRefresh: () => void;
  isSuperAdmin: boolean;
}) {
  const [syncing, setSyncing] = useState(false);
  const [autoSync, setAutoSync] = useState(connected.auto_sync_enabled);
  const [interval, setInterval_] = useState(connected.sync_interval_minutes);
  const [activeStartHour, setActiveStartHour] = useState<number | null>(connected.sync_active_start_hour);
  const [activeEndHour, setActiveEndHour] = useState<number | null>(connected.sync_active_end_hour);
  const hasActiveHours = activeStartHour != null && activeEndHour != null;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleRefresh = async () => {
    setSyncing(true);
    try {
      await api.triggerSync(connected.id);

      // Poll for completion
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getSyncStatus(connected.id);
          if (status.sync_status === 'succeeded' || status.sync_status === 'failed' || status.sync_status === 'idle') {
            setSyncing(false);
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            onRefresh();
          }
        } catch {
          setSyncing(false);
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 2000);
    } catch (err) {
      setSyncing(false);
    }
  };

  const saveAutoSync = async (params: {
    enabled: boolean;
    intervalMinutes: number;
    startHour: number | null;
    endHour: number | null;
  }) => {
    try {
      await api.updateAutoSync(connected.id, {
        enabled: params.enabled,
        intervalMinutes: params.intervalMinutes as any,
        activeStartHour: params.startHour,
        activeEndHour: params.endHour,
      });
    } catch (err) {
      console.error('Failed to update auto-sync:', err);
    }
  };

  const handleAutoSyncToggle = async () => {
    const newEnabled = !autoSync;
    setAutoSync(newEnabled);
    await saveAutoSync({ enabled: newEnabled, intervalMinutes: interval, startHour: activeStartHour, endHour: activeEndHour });
  };

  const handleIntervalChange = async (newInterval: number) => {
    setInterval_(newInterval);
    if (autoSync) {
      await saveAutoSync({ enabled: true, intervalMinutes: newInterval, startHour: activeStartHour, endHour: activeEndHour });
    }
  };

  const handleActiveHoursToggle = async () => {
    if (hasActiveHours) {
      setActiveStartHour(null);
      setActiveEndHour(null);
      if (autoSync) await saveAutoSync({ enabled: true, intervalMinutes: interval, startHour: null, endHour: null });
    } else {
      setActiveStartHour(5);
      setActiveEndHour(22);
      if (autoSync) await saveAutoSync({ enabled: true, intervalMinutes: interval, startHour: 5, endHour: 22 });
    }
  };

  const handleActiveHourChange = async (field: 'start' | 'end', value: number) => {
    const newStart = field === 'start' ? value : activeStartHour;
    const newEnd = field === 'end' ? value : activeEndHour;
    if (field === 'start') setActiveStartHour(value);
    else setActiveEndHour(value);
    if (autoSync) await saveAutoSync({ enabled: true, intervalMinutes: interval, startHour: newStart, endHour: newEnd });
  };

  return (
    <div className="integration-card integration-card--connected">
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          {def.logoSrc ? (
            <img className="integration-logo__img" src={def.logoSrc} alt={def.logoAlt || `${def.name} logo`} />
          ) : (
            <div className="integration-logo__fallback">{def.name.charAt(0)}</div>
          )}
        </div>
        <div className="integration-title">
          <h3>{def.name}</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">{def.category}</span>
            <span className="integration-capability-pill">Schedule + court sync</span>
          </div>
        </div>
        <div className="integration-card-header__right">
          <span className="integration-state-pill integration-state-pill--connected">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Connected
          </span>
          <SyncStatusBadge status={connected.sync_status || 'idle'} />
        </div>
      </div>

      <div className="integration-meta">
        <div className="integration-meta__item">
          <div className="integration-meta__label">Last synced</div>
          <div className="integration-meta__value">{formatTimeAgo(connected.last_sync_at)}</div>
        </div>
        <div className="integration-meta__item">
          <div className="integration-meta__label">External ID</div>
          <div className="integration-meta__value integration-meta__value--mono">{connected.external_id}</div>
        </div>
      </div>

      {connected.last_error && connected.sync_status === 'failed' && (
        <div className="integration-error-banner" role="alert">
          <strong>Last error:</strong> {connected.last_error}
        </div>
      )}

      {/* Superadmin: auto-sync controls */}
      {isSuperAdmin && (
        <div className="integration-admin-controls">
          <div className="integration-admin-controls__row">
            <label className="integration-admin-controls__checkbox">
              <input
                type="checkbox"
                checked={autoSync}
                onChange={handleAutoSyncToggle}
              />
              Auto-sync
            </label>
            {autoSync && (
              <select
                value={interval}
                onChange={e => handleIntervalChange(parseInt(e.target.value))}
                className="integration-admin-controls__select"
              >
                <option value={5}>Every 5 min</option>
                <option value={15}>Every 15 min</option>
                <option value={30}>Every 30 min</option>
                <option value={60}>Every 60 min</option>
              </select>
            )}
          </div>
          {autoSync && (
            <div className="integration-admin-controls__row integration-admin-controls__row--sub">
              <label className="integration-admin-controls__checkbox">
                <input
                  type="checkbox"
                  checked={hasActiveHours}
                  onChange={handleActiveHoursToggle}
                />
                Active hours only
              </label>
              {hasActiveHours && (
                <div className="integration-admin-controls__hours">
                  <select
                    value={activeStartHour ?? 5}
                    onChange={e => handleActiveHourChange('start', parseInt(e.target.value))}
                    className="integration-admin-controls__select"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}</option>
                    ))}
                  </select>
                  <span className="integration-admin-controls__hours-sep">to</span>
                  <select
                    value={activeEndHour ?? 22}
                    onChange={e => handleActiveHourChange('end', parseInt(e.target.value))}
                    className="integration-admin-controls__select"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="integration-footer integration-footer--connected">
        <button
          className="btn btn-primary"
          onClick={handleRefresh}
          disabled={syncing}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {syncing ? (
            <>
              <span className="integration-btn-spinner" />
              Syncing...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2v4h-4M2 14v-4h4" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.5 6A6 6 0 0 0 3.8 3.8L2 6M2.5 10a6 6 0 0 0 9.7 2.2L14 10" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Sync Now
            </>
          )}
        </button>
        <button className="btn btn-secondary integration-disconnect-btn" onClick={onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}

function IntegrationCard({
  integration,
  onSetup
}: {
  integration: IntegrationDef;
  onSetup?: () => void;
}) {
  const isActive = integration.status === 'beta' || integration.status === 'available';

  return (
    <div className={`integration-card ${isActive ? 'integration-card--available' : 'integration-card--coming-soon'}`}>
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          {integration.logoSrc ? (
            <img className="integration-logo__img" src={integration.logoSrc} alt={integration.logoAlt || `${integration.name} logo`} />
          ) : (
            <div className="integration-logo__fallback">{integration.name.charAt(0)}</div>
          )}
        </div>
        <div className="integration-title">
          <h3>{integration.name}</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">{integration.category}</span>
            <span className="integration-capability-pill">Schedule + court sync</span>
          </div>
        </div>
        <span className={`integration-badge ${integration.status}`}>
          {integration.status === 'beta' ? 'Beta' :
           integration.status === 'available' ? 'Available' : 'Coming Soon'}
        </span>
      </div>

      <p className="integration-description">{integration.description}</p>

      <div className="integration-support-note">
        Currently supported: <strong>real-time schedule syncing</strong> and <strong>court syncing</strong>.
      </div>

      <div className="integration-footer">
        {isActive ? (
          <button className="btn btn-primary" onClick={onSetup}>
            Connect
          </button>
        ) : (
          <>
            <button className="btn btn-secondary" disabled>
              Notify Me
            </button>
            <span className="integration-eta">Expected Q2 2026</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsScreen() {
  const [activeIntegration, setActiveIntegration] = useState<string | null>(null);
  const [connectedIntegrations, setConnectedIntegrations] = useState<ConnectedIntegration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const { refreshData } = useBusiness();
  const { isSuperAdmin } = useAuth();
  const { showModal } = useModal();

  const fetchIntegrations = async () => {
    try {
      const result = await api.getIntegrations();
      setConnectedIntegrations(result.integrations.filter(i => i.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const handleSetupIntegration = (integrationId: string) => {
    setActiveIntegration(integrationId);
  };

  const handleCloseIntegration = () => {
    refreshData();
    fetchIntegrations();
    setActiveIntegration(null);
  };

  const handleDisconnect = async (integration: ConnectedIntegration) => {
    try {
      const def = [...integrationDefs, ...internalIntegrationDefs].find(d => d.id === integration.integration_type);
      const name = def?.name || integration.integration_type;
      await showModal({
        title: `Disconnect ${name}?`,
        message: `This won't delete your courts or availability data.`,
        type: 'danger',
        confirmLabel: 'Disconnect',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          await api.deleteIntegration(integration.id);
          fetchIntegrations();
        }
      });
    } catch (err) {
      console.error('Failed to disconnect:', err);
    }
  };

  const handleRefresh = () => {
    refreshData();
    fetchIntegrations();
  };

  // Show CourtReserve integration flow
  if (activeIntegration === 'courtreserve') {
    return (
      <DashboardLayout>
        <CourtReserveIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'courtreserve_auto_approval') {
    return (
      <DashboardLayout>
        <CourtReserveAutoApprovalIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'playbypoint_auto_approval') {
    return (
      <DashboardLayout>
        <PlayByPointAutoApprovalIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  // Show PlayByPoint integration flow
  if (activeIntegration === 'playbypoint') {
    return (
      <DashboardLayout>
        <PlayByPointIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  // Show SmashPark integration flow (superadmin only)
  if (activeIntegration === 'smashpark') {
    return (
      <DashboardLayout>
        <SmashParkIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'alleytrak') {
    return (
      <DashboardLayout>
        <BlueSkyIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'courtsandcommons') {
    return (
      <DashboardLayout>
        <CourtsAndCommonsIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'squareappointments') {
    return (
      <DashboardLayout>
        <PryorsPizzaKitchenIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'chickennpickle') {
    return (
      <DashboardLayout>
        <ChickenNPickleIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  if (activeIntegration === 'prairiebrewing') {
    return (
      <DashboardLayout>
        <PrairieBrewingIntegration onClose={handleCloseIntegration} />
      </DashboardLayout>
    );
  }

  // Build a set of connected integration types
  const connectedTypes = new Set(connectedIntegrations.map(i => i.integration_type));

  return (
    <DashboardLayout>
      <div className="integrations-page">
        <div className="integrations-page__header">
          <div className="integrations-page__title-block">
            <h1>Integrations</h1>
            <p>Connect booking platforms to keep availability and court mappings in sync.</p>
          </div>
        </div>

        <div className="integrations-grid integrations-grid--all">
          {loadingIntegrations
            ? Array.from({ length: integrationDefs.length }).map((_, idx) => (
                <div key={idx} className="integration-card integration-card--skeleton" />
              ))
            : integrationDefs
                .slice()
                .sort((a, b) => Number(connectedTypes.has(b.id)) - Number(connectedTypes.has(a.id)))
                .map((def) => {
                  const connected = connectedIntegrations.find(i => i.integration_type === def.id);
                  return connected ? (
                    <ConnectedCard
                      key={connected.id}
                      def={def}
                      connected={connected}
                      onDisconnect={() => handleDisconnect(connected)}
                      onRefresh={handleRefresh}
                      isSuperAdmin={isSuperAdmin}
                    />
                  ) : (
                    <IntegrationCard
                      key={def.id}
                      integration={def}
                      onSetup={
                        (def.status === 'beta' || def.status === 'available')
                          ? () => handleSetupIntegration(def.id)
                          : undefined
                      }
                    />
                  );
                })}
        </div>

        {/* Superadmin-only: internal one-off integrations */}
        {isSuperAdmin && (
          <>
            <div className="integrations-page__section-header" style={{ marginTop: '40px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                Internal Integrations
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
                Venue-specific integrations not available to all customers.
              </p>
            </div>
            <div className="integrations-grid integrations-grid--all">
              {loadingIntegrations
                ? <div className="integration-card integration-card--skeleton" />
                : internalIntegrationDefs.map((def) => {
                    const connected = connectedIntegrations.find(i => i.integration_type === def.id);
                    if (connected && def.id === 'courtreserve_auto_approval') {
                      return (
                        <CourtReserveAutoApprovalConnectedCard
                          key={connected.id}
                          integration={connected}
                          onDisconnect={() => handleDisconnect(connected)}
                          onRefresh={handleRefresh}
                        />
                      );
                    }

                    if (connected && def.id === 'playbypoint_auto_approval') {
                      return (
                        <PlayByPointAutoApprovalConnectedCard
                          key={connected.id}
                          integration={connected}
                          onDisconnect={() => handleDisconnect(connected)}
                          onRefresh={handleRefresh}
                        />
                      );
                    }

                    if (def.id === 'courtreserve_auto_approval') {
                      return (
                        <CourtReserveAutoApprovalAvailableCard
                          key={def.id}
                          onSetup={() => handleSetupIntegration(def.id)}
                        />
                      );
                    }

                    if (def.id === 'playbypoint_auto_approval') {
                      return (
                        <PlayByPointAutoApprovalAvailableCard
                          key={def.id}
                          onSetup={() => handleSetupIntegration(def.id)}
                        />
                      );
                    }

                    return connected ? (
                      <ConnectedCard
                        key={connected.id}
                        def={def}
                        connected={connected}
                        onDisconnect={() => handleDisconnect(connected)}
                        onRefresh={handleRefresh}
                        isSuperAdmin={isSuperAdmin}
                      />
                    ) : (
                      <IntegrationCard
                        key={def.id}
                        integration={def}
                        onSetup={() => handleSetupIntegration(def.id)}
                      />
                    );
                  })}
            </div>
          </>
        )}

        <div className="integrations-request integrations-request--v2">
          <div className="request-content">
            <h3>Need a different integration?</h3>
            <p>Tell us what booking system you use and we will prioritize it.</p>
            <a href="mailto:support@picklecourts.rent" className="btn btn-primary">
              Request Integration
            </a>
          </div>
        </div>

        {/* Spinner animation */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </DashboardLayout>
  );
}
